/**
 * Global providers wrapper (Theme, Cart, Campaign, Funnel + TanStack Query).
 * Dehydrated query state comes from +data → pageContext.data.dehydratedState.
 */

import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import {
  QueryClient,
  QueryClientProvider,
  HydrationBoundary,
  type DehydratedState,
} from '@tanstack/react-query';
import { usePageContext } from 'vike-react/usePageContext';
import { StorefrontI18nProvider } from '@/lib/i18n/StorefrontI18nProvider';
import { ThemeProvider } from '@template-project/afrodita-components';
import { CacheProvider } from '@emotion/react';
import '@template-project/afrodita-components/theme/theme-variables.css';
import { DeviceProvider } from '@template-project/storefront-shared';
import { CartProvider } from '@/lib/context/cart-context';
import { ToastProvider } from '@/lib/context/toast-context';
import { CampaignProvider } from '@template-project/storefront-campaign';
import { CheckoutReturnListener } from '@/components/CheckoutReturnListener/CheckoutReturnListener';
import { CheckoutAbandonedModal } from '@/components/CheckoutAbandonedModal/CheckoutAbandonedModal';
import { env } from '@/lib/utils/env';
import { FunnelRuntimeProvider } from '@/app/FunnelRuntimeProvider';
import { defaultTheme } from '@/styles/theme/default-theme';
import { ClientOnly } from '@/lib/ssr/client-only';
import { createEmotionCache } from '@/lib/ssr/emotion-cache';
import { getBrowserDocument } from '@/lib/ssr/safety';
import '../src/app/globals.css';

export default function Wrapper({ children }: { children: ReactNode }) {
  const pageContext = usePageContext();
  const dehydratedState = (pageContext.data as { dehydratedState?: DehydratedState } | undefined)
    ?.dehydratedState;

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: true,
            retry: 1,
          },
        },
      }),
  );

  // Per React tree instance — new cache each SSR request; stable on client hydrate.
  const [emotionCache] = useState(() => createEmotionCache());

  useEffect(() => {
    const doc = getBrowserDocument();
    if (!doc) return;
    doc.documentElement.classList.remove('dark');
    try {
      // localStorage is browser-only; gated by getBrowserDocument above.
      globalThis.localStorage?.removeItem('tf-theme');
    } catch {
      // ignore
    }
  }, []);

  const commerce = (
    <CartProvider>
      <ClientOnly>
        <CheckoutReturnListener />
        <CheckoutAbandonedModal />
      </ClientOnly>
      <CampaignProvider
        config={{
          apiBaseUrl: env.STOREFRONT_BFF_URL,
          tenantId: env.CAMPAIGNS_TENANT_ID,
          templatesApiBaseUrl: env.STOREFRONT_BFF_URL,
        }}
      >
        <FunnelRuntimeProvider>
          {children}
        </FunnelRuntimeProvider>
      </CampaignProvider>
    </CartProvider>
  );

  const themed = (
    <CacheProvider value={emotionCache}>
      <ThemeProvider theme={defaultTheme}>
        <ToastProvider>
          <ClientOnly fallback={commerce}>
            <DeviceProvider>{commerce}</DeviceProvider>
          </ClientOnly>
        </ToastProvider>
      </ThemeProvider>
    </CacheProvider>
  );

  const withQuery = (
    <QueryClientProvider client={queryClient}>
      {dehydratedState ? (
        <HydrationBoundary state={dehydratedState}>{themed}</HydrationBoundary>
      ) : (
        themed
      )}
    </QueryClientProvider>
  );

  return <StorefrontI18nProvider>{withQuery}</StorefrontI18nProvider>;
}
